import { storage } from './storage';

/**
 * Le uova del Profilo: quali esistono, come si chiamano, e quale ti tocca.
 *
 * ⚠️ Tutto questo è un SEGNAPOSTO. Nessun backend conosce le uova, non esiste
 * un inventario, e l'assegnazione vive nel browser. Vedi ADR-0038 § «cosa è
 * vero e cosa è segnaposto».
 */

export type Rarity = 'common' | 'rare' | 'epic';

/** Chiave stabile della variante. Va nello storage, quindi NON si rinomina. */
export type EggId = 'crimson' | 'space' | 'jungle' | 'rays' | 'heaven' | 'plain';

export interface Egg {
  id: EggId;
  rarity: Rarity;
  /**
   * Filtro CSS applicato all'unica immagine che abbiamo.
   *
   * **Provvisorio, e si vede.** C'è un solo `egg.png`, quello cremisi: gli altri
   * colori sono lo stesso disegno con la tinta ruotata. Regge per provare la
   * rotazione, non per il rilascio — una rotazione di tinta sposta TUTTO,
   * comprese ombre e riflessi, quindi il volume dell'uovo si legge un po'
   * diverso a ogni variante. Il giorno che arrivano i disegni veri, questo
   * campo sparisce e al suo posto va il percorso dell'immagine.
   */
  filter?: string;
  /**
   * Classe CSS alternativa al filtro fisso, per le varianti che si muovono.
   * Serve all'arcobaleno: un colore che cambia non è esprimibile con un filtro
   * statico, e un'animazione non può stare in una stringa inline.
   */
  filterClass?: string;
  /**
   * Quante parti su `TOTAL` di utenti ricevono questa variante.
   *
   * È qui che la rarità smette di essere una parola e diventa un fatto: senza
   * pesi le sei uova sarebbero equiprobabili, e «Epico» non significherebbe
   * nulla — sarebbe solo un'etichetta viola su qualcosa di comune quanto il
   * resto.
   */
  weight: number;
}

/**
 * Il totale è FISSO e non la somma calcolata dei pesi.
 *
 * Se fosse la somma, aggiungere una variante cambierebbe il denominatore e
 * quindi **l'uovo di tutti** — la mattina dopo mezza community scrive che il
 * suo uovo è sparito. Con un totale fisso, aggiungere significa **togliere
 * quota a qualcun altro**, che è una decisione esplicita invece di un effetto
 * collaterale.
 *
 * Regola pratica: prendi la quota dall'**ultima** voce dell'elenco. I confini
 * si calcolano in ordine, quindi tutto ciò che sta prima della voce toccata
 * resta fermo, e si muovono solo gli utenti di quella.
 */
const TOTAL = 10000;

/**
 * L'elenco, in ordine di rotazione.
 *
 * **Append-only**: le chiavi finiscono nello storage del browser, quindi
 * togliere o rinominare una voce lascerebbe utenti con un `id` che non esiste
 * più. Si aggiunge in fondo, non si riordina.
 */
export const EGGS: readonly Egg[] = [
  { id: 'crimson', rarity: 'rare', weight: 1250 },
  { id: 'space', rarity: 'rare', weight: 1250, filter: 'hue-rotate(230deg)' },
  { id: 'jungle', rarity: 'rare', weight: 1250, filter: 'hue-rotate(140deg)' },
  { id: 'rays', rarity: 'rare', weight: 1250, filter: 'hue-rotate(60deg) saturate(1.3)' },
  {
    id: 'heaven',
    rarity: 'epic',
    weight: 500,
    // L'arcobaleno non è un colore fermo: nessun filtro statico lo produce, e
    // un'immagine sola non può esserlo. Qui la tinta RUOTA di continuo — è
    // l'unico modo di suggerirlo con quello che abbiamo, ed è anche l'unica
    // variante che si muove, il che le dà da sola l'aria di essere speciale.
    filterClass: 'egg-rainbow',
  },
  {
    id: 'plain',
    rarity: 'common',
    // Il comune è il più diffuso: 45%. È lui a dare significato agli altri —
    // se il caso più frequente non è chiaramente il più frequente, «raro» non
    // ha un metro.
    weight: 4500,
    // Desaturato e schiarito: il comune non ha un colore, è la sua definizione.
    filter: 'saturate(0.12) brightness(1.28)',
  },
] as const;

/**
 * Chiavi di locale. Nome e colore si TRADUCONO: in italiano è «Uovo Crimson»,
 * in inglese «Crimson Egg» — cambia l'ordine, non solo la parola. Il colore
 * resta un nome proprio in entrambe (Crimson, Space…) tranne il comune, che un
 * colore proprio non ce l'ha.
 */
export const eggNameKey = (id: EggId) => `egg_name.${id}`;
export const eggColorKey = (id: EggId) => `egg_color.${id}`;
/**
 * La frase che l'uovo dice sul PROPRIO colore.
 *
 * Sta dentro la variante e non fra le frasi generiche perché è l'unica che
 * parla di come l'uovo è fatto: lasciata comune, a chi ha l'uovo blu comparirebbe
 * un commento entusiasta sul rosso.
 */
export const eggLineKey = (id: EggId) => `egg_line.${id}`;

export function getEggById(id: string | null | undefined): Egg | null {
  return EGGS.find((e) => e.id === id) ?? null;
}

/**
 * Impronta numerica stabile di una stringa (FNV-1a a 32 bit).
 *
 * Serve un numero che dallo stesso id dia **sempre** lo stesso risultato, su
 * ogni dispositivo e per sempre. `Math.random()` no per ovvi motivi, ma nemmeno
 * qualcosa di più furbo: qui non serve robustezza crittografica, serve che il
 * risultato non cambi mai — e una funzione di tre righe che tutti capiscono
 * è più difficile da rompere per sbaglio di una importata.
 *
 * `Math.imul` e non `*`: la moltiplicazione normale in JavaScript passa per i
 * float a 64 bit e perde i bit bassi oltre i 2^53, che è esattamente dove
 * questo calcolo lavora. Senza, l'impronta collassa su pochi valori.
 */
function fingerprint(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * L'uovo di un utente — derivato dal suo id, non sorteggiato né memorizzato.
 *
 * Stesso utente, stesso uovo: su ogni dispositivo, dopo ogni reinstallazione,
 * per sempre. Senza una riga di backend e senza niente da salvare — il che
 * significa anche che non c'è niente che possa perdersi o disallinearsi.
 *
 * Il limite, da sapere prima di appoggiarci qualcosa di più serio: essendo
 * **derivato**, non è posseduto. Non si può regalare, scambiare, né far
 * schiudere in qualcos'altro. Il giorno che una di queste cose serve, l'uovo
 * deve diventare un dato del server — e questa funzione sparisce.
 *
 * Il prefisso `qlash-egg-v1:` isola questo calcolo: se un domani si volesse
 * ri-sorteggiare tutti (o derivare un'altra cosa dallo stesso id), si cambia il
 * prefisso invece di toccare la funzione.
 */
export function eggForUser(userId: string | number | null | undefined): Egg {
  if (userId === null || userId === undefined || userId === '') return EGGS[0];

  let n = fingerprint(`qlash-egg-v1:${userId}`) % TOTAL;
  for (const egg of EGGS) {
    if (n < egg.weight) return egg;
    n -= egg.weight;
  }
  // Raggiungibile solo se la somma dei pesi è minore di TOTAL, cioè se qualcuno
  // ha modificato l'elenco senza rifare i conti. Meglio l'uovo comune che un
  // errore in faccia all'utente per una tabella sbagliata.
  return EGGS[EGGS.length - 1];
}

/* ------------------------------------------------------------------------ *
 * Scavalco di prova — impalcatura, non funzione.
 *
 * Il tasto accanto al nome scrive qui la variante scelta, così si possono
 * guardare tutte senza sei account. Sparisce insieme al tasto il giorno che
 * l'uovo è un dato vero.
 * ------------------------------------------------------------------------ */

const OVERRIDE_KEY = 'qlash.egg_override';

export function loadOverride(): Egg | null {
  return getEggById(storage.get(OVERRIDE_KEY));
}

export function saveOverride(id: EggId): void {
  storage.set(OVERRIDE_KEY, id);
}

/** La variante successiva, ciclica. */
export function nextEgg(current: EggId): Egg {
  const i = EGGS.findIndex((e) => e.id === current);
  return EGGS[(i + 1) % EGGS.length];
}
